import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import {
  RedeemableMzusd,
  useRedeemMzusd,
  useRedeemableMzusdBalances,
} from '@/app/components/PortfolioPageComponents/hooks/useRedeemMzusd';
import { refreshPortfolioWithdrawalsUi } from '@/app/components/PortfolioPageComponents/refreshPortfolioWithdrawalsUi';
import { TransactionCompletedModalTextType } from '@/app/shared/constants/modals';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { cn } from '@/app/shared/lib/cn';
import { preloadModals } from '@/app/shared/lib/preloadModals';
import { Button } from '@/app/shared/ui/Button';
import DataTable, {
  DataTableColumn,
  DataTableStacked,
  DataTableValue,
} from '@/app/shared/ui/DataTable.tsx';

const SequentialStepModal = lazy(() => import('@/app/components/Modal/SequentialStepModal'));
const TransactionCompleted = lazy(() => import('@/app/components/Modal/TransactionCompleted'));

const desktopGridClassName =
  'min-[960px]:grid min-[960px]:w-full min-[960px]:min-w-0 min-[960px]:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_auto] min-[960px]:items-center min-[960px]:gap-2 min-[960px]:px-5 xl:gap-4';

const redeemButtonClass = 'h-9 shrink-0 px-4 text-sm';

interface RedeemRowButtonProps {
  item: RedeemableMzusd;
  onSettled?: () => void;
  className?: string;
}

const RedeemRowButton = ({ item, onSettled, className }: RedeemRowButtonProps) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const refreshWithdrawUi = useCallback(() => {
    void refreshPortfolioWithdrawalsUi(queryClient);
    onSettled?.();
  }, [onSettled, queryClient]);

  const {
    handleSend,
    steps,
    transactionStatusData,
    txHash,
    errorMessage,
    resetTransactionState,
    isReady,
    amountFormatted,
  } = useRedeemMzusd({
    item,
    enabled: item.canRedeemOnChain,
  });

  const { isSuccess: isTxSuccess, isLoading: isProcessing } = transactionStatusData;
  const canRedeem = item.canRedeemOnChain && isReady && !isProcessing;

  useEffect(() => {
    if (!isTxSuccess) return;

    setIsModalOpen(false);
    setIsSuccessModalOpen(true);
  }, [isTxSuccess]);

  const handleSuccessClose = () => {
    resetTransactionState();
    setIsSuccessModalOpen(false);
    refreshWithdrawUi();
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_POSITIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_PORTFOLIO] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_TRANSACTIONS] });
  };

  const handleRedeem = () => {
    if (!canRedeem) return;
    preloadModals();
    handleSend();
    setIsModalOpen(true);
  };

  return (
    <>
      <Button
        onClick={handleRedeem}
        disabled={!canRedeem}
        variant='outline'
        className={cn(redeemButtonClass, className)}
        title={!item.canRedeemOnChain ? 'Amount is too small to redeem' : undefined}
      >
        Redeem
      </Button>

      <Suspense fallback={null}>
        <SequentialStepModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            refreshWithdrawUi();
          }}
          title='Redeem MZUSD'
          subtitle={`Redeem ${item.balanceFormatted} MZUSD from ${item.vaultName} to USDC.`}
          steps={steps}
          errorMessage={errorMessage}
          onRetry={() => void handleSend()}
        />
      </Suspense>

      <Suspense fallback={null}>
        <TransactionCompleted
          isOpen={isSuccessModalOpen}
          onClose={handleSuccessClose}
          txHash={txHash}
          amount={amountFormatted}
          symbol='MZUSD'
          symbolOut='USDC'
          isReversed={true}
          textType={TransactionCompletedModalTextType.CLAIM}
        />
      </Suspense>
    </>
  );
};

const COLUMNS: DataTableColumn<RedeemableMzusd>[] = [
  {
    id: 'vault',
    header: 'Vault',
    cell: (row) => <DataTableStacked primary={row.vaultName} secondary='MZUSD balance' />,
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: (row) => <DataTableValue value={`${row.balanceFormatted} MZUSD`} />,
  },
  {
    id: 'status',
    header: 'Status',
    cell: () => (
      <div className='flex min-w-0 items-center gap-2'>
        <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-brand-1000' />
        <span className='truncate text-base text-brand-1000'>Redeemable</span>
      </div>
    ),
  },
];

const RedeemMzusdTable = () => {
  const { redeemable, refetch, isConnected } = useRedeemableMzusdBalances();

  if (!isConnected || redeemable.length === 0) return null;

  return (
    <div className='flex min-w-0 flex-col gap-3'>
      <div className='flex flex-col gap-3 min-[960px]:flex-row min-[960px]:items-start min-[960px]:justify-between'>
        <div className='flex flex-col gap-1'>
          <h3 className='text-base font-normal text-brand-1100'>MZUSD to redeem</h3>
          <p className='text-base leading-6 text-gray-400'>
            Leftover MZUSD after an interrupted withdraw, or received outside the app. Each vault has its own
            MZUSD.
          </p>
        </div>
        <span className='shrink-0 text-base leading-6 text-gray-400'>
          {redeemable.length} {redeemable.length === 1 ? 'position' : 'positions'}
        </span>
      </div>

      <DataTable
        data={redeemable}
        getRowKey={(row) => row.mzusd}
        desktopGridClassName={desktopGridClassName}
        pagination={false}
        desktopRowClassName='h-[74px] py-4'
        mobileRowClassName='p-[12px_16px]'
        headerTrailing={
          <Button
            variant='outline'
            className={cn(redeemButtonClass, 'pointer-events-none invisible')}
            tabIndex={-1}
            aria-hidden
          >
            Redeem
          </Button>
        }
        columns={COLUMNS}
        rowTrailing={(row) => (
          <RedeemRowButton item={row} onSettled={() => void refetch()} />
        )}
        renderMobile={(row) => (
          <div className='flex flex-col gap-[21px]'>
            <div className='flex items-start justify-between gap-4'>
              <div className='flex min-w-0 flex-col gap-0.5'>
                <span className='text-base text-gray-200'>{row.vaultName}</span>
                <span className='text-sm text-gray-400'>MZUSD balance</span>
              </div>
              <div className='flex items-center gap-2'>
                <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-brand-1000' />
                <span className='text-base text-brand-1000'>Redeemable</span>
              </div>
            </div>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className='text-sm font-medium tracking-[0.075em] text-gray-500'>Amount</span>
              <DataTableValue value={`${row.balanceFormatted} MZUSD`} />
            </div>
            <RedeemRowButton item={row} onSettled={() => void refetch()} className='w-full' />
          </div>
        )}
      />
    </div>
  );
};

export default RedeemMzusdTable;
